import { ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Dropdown, Option, SelectWrapper } from './styles'

export interface SelectOption {
  label: string
  value: string | number
}

interface SelectProps {
  children: ReactNode
  options: SelectOption[]
  onChange?: (option: SelectOption) => void | Promise<any>
}

const Select = ({ children, options, onChange }: SelectProps) => {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, isTop: false })
  const triggerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const updatePosition = () => {
    if (!triggerRef.current || !dropdownRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const dropdownRect = dropdownRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight

    // 计算垂直方向
    const shouldShowOnTop = triggerRect.bottom + dropdownRect.height > viewportHeight
    const verticalPosition = shouldShowOnTop ? triggerRect.top - dropdownRect.height : triggerRect.bottom

    // 计算水平方向
    let horizontalPosition = triggerRect.left
    if (horizontalPosition + dropdownRect.width > viewportWidth) {
      horizontalPosition = viewportWidth - dropdownRect.width - 30 // 16px 作为安全边距
    }
    if (horizontalPosition < 16) {
      horizontalPosition = 16
    }

    setPosition({
      left: horizontalPosition,
      top: verticalPosition,
      isTop: shouldShowOnTop,
    })
  }

  useEffect(() => {
    if (visible) {
      updatePosition()
      window.addEventListener('scroll', updatePosition)
      window.addEventListener('resize', updatePosition)
    }

    return () => {
      window.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
    }
  }, [visible])

  // 添加点击外部区域处理
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!triggerRef.current?.contains(event.target as Node) && !dropdownRef.current?.contains(event.target as Node)) {
        setVisible(false)
      }
    }

    if (visible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [visible])

  const handleClick = (option: SelectOption) => {
    onChange?.(option)
    setVisible(false)
  }

  return (
    <>
      <SelectWrapper ref={triggerRef} onClick={() => setVisible(!visible)}>
        {children}
      </SelectWrapper>
      {createPortal(
        <Dropdown
          ref={dropdownRef}
          $visible={visible}
          $isTop={position.isTop}
          style={{
            left: position.left,
            top: position.top,
          }}
        >
          {options.map((option) => (
            <Option key={option.value} onClick={() => handleClick(option)}>
              {option.label}
            </Option>
          ))}
        </Dropdown>,
        document.body,
      )}
    </>
  )
}

export default Select
